import { Component, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import {
  ConfirmationModalComponent,
  FormDropdownComponent,
  FormInputComponent,
  MetricsReadingDetailsComponent,
} from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { AdaaBoolean, PageMode } from "../../../../shared/constants/enums";
import { DataEntryMetricModel, PropTypeModelType, ValueText } from "../../../../shared/models";
import { LanguageService, MetricApiService, PropertiesService, SystemLayoutService } from "../../../../shared/services";

@Component({
  selector: "adaa-data-entry-metrics-editor",
  imports: [
    TranslatePipe,
    TranslateTokenPipe,
    FormInputComponent,
    FormDropdownComponent,
    MetricsReadingDetailsComponent,
  ],
  templateUrl: "./data-entry-metrics-editor.html",
  styleUrl: "./data-entry-metrics-editor.scss",
})
export class DataEntryMetricsEditorComponent implements OnInit {
  private readonly _metricsApiService = inject(MetricApiService);
  private readonly _propsService = inject(PropertiesService);
  private readonly _languageService = inject(LanguageService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _modalService = inject(NgbModal);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _router = inject(Router);

  private _untilDestroyed = AdaaHelper.untilDestroyed();

  id = input.required<number>();
  pageMode = input.required<string>();
  isMonitoring = input.required<boolean>();

  PageMode = PageMode;

  month = signal<number>(1);
  year = signal<number>(new Date().getFullYear());

  months = signal<ValueText[]>([]);
  years = signal<ValueText[]>([]);

  metric = signal<DataEntryMetricModel | undefined>(undefined);
  dataTypes = signal<PropTypeModelType[]>([]);
  value = signal<number | null | undefined>(undefined);

  metricReadingDetails = viewChild.required<MetricsReadingDetailsComponent>("metricReadingDetails");

  public getDataType(value: number | undefined) {
    const field = AdaaHelper.getFieldLanguage("name");
    const type = this.dataTypes().find((type) => type.id === value);
    if (type) {
      return type[field as keyof PropTypeModelType];
    }
    return "";
  }

  public ngOnInit() {
    this._setMonthsYearsDdl();
    this._initPage();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this._router.navigateByUrl("/console/data-entry/metrics"),
    });
  }

  public addReading() {
    if (!this.value()) {
      this._toastrService.error(this._languageService.translate("data_entry.value_empty"));
      return;
    }

    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "data_entry.metric_add_edit_title";
    modal.componentInstance.title = "data_entry.metric_add_edit_information";

    modal.result.then((e) => {
      if (e) {
        this.metricReadingDetails().addReading(this.value(), this.month(), this.year());
        this.value.set(null);
      }
    });
  }

  private _initPage() {
    this._propsService.getPropById(Constants.CONSTANT_MEASUREMENT_UNIT_METRICS).subscribe({
      next: (response) => this.dataTypes.set(response.responseData),
    });

    if (!this.id()) return;

    this._metricsApiService.getById(this.id()).subscribe({
      next: (response) => {
        if (this.pageMode() === PageMode.edit && this._checkNotAllowedToEdit(response.responseData)) {
          this._router.navigateByUrl("/console/data-entry/metrics");
          return;
        }

        this.metric.set(response.responseData);
      },
    });
  }

  private _setMonthsYearsDdl() {
    const months: ValueText[] = [];
    for (let index = 1; index <= 12; index++) months.push({ value: index, text: index.toString() });
    this.months.set(months);

    const years: ValueText[] = [];
    const currentYear = new Date().getFullYear();
    for (let index = currentYear; index >= 2000; index--) years.push({ value: index, text: index.toString() });
    this.years.set(years);
  }

  private _checkNotAllowedToEdit(metric: DataEntryMetricModel): boolean {
    if (this.isMonitoring() && metric.type !== Constants.METRICS_TYPE.DIMENSION) return true;
    else if (!this.isMonitoring() && metric.type === Constants.METRICS_TYPE.DIMENSION) return true;
    else if (AdaaHelper.isPMOEntity()) {
      return metric.synced === AdaaBoolean.Y;
    } else {
      return metric.synced === AdaaBoolean.Y || metric.type === Constants.METRICS_TYPE.GLOBAL;
    }
  }
}
